import { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useEnterAsTab } from '../hooks/useEnterAsTab';

export const EmployeeCreationView = ({ config, handleItemSelect, employeeId }: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeCompany = useStore((state) => state.activeCompany);
  
  const [name, setName] = useState('');
  const [empId, setEmpId] = useState('');
  const [department, setDepartment] = useState('');
  const [designation, setDesignation] = useState('');
  const [joiningDate, setJoiningDate] = useState('');
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');
  const [bankName, setBankName] = useState('');
  const [bankAccountNo, setBankAccountNo] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  
  // Salary Structure
  const [basic, setBasic] = useState<number>(0);
  const [hra, setHra] = useState<number>(0);
  const [conveyance, setConveyance] = useState<number>(0);
  const [otherAllowances, setOtherAllowances] = useState<number>(0);
  const [pfDeduction, setPfDeduction] = useState<number>(0);
  const [esiDeduction, setEsiDeduction] = useState<number>(0);
  const [tdsDeduction, setTdsDeduction] = useState<number>(0);

  const nameInputRef = useRef<HTMLInputElement>(null);
  useEnterAsTab(containerRef);

  useEffect(() => {
    if (employeeId) {
      db.employees.get(employeeId).then(emp => {
        if (emp) {
          setName(emp.name);
          setEmpId(emp.employeeId);
          setDepartment(emp.department);
          setDesignation(emp.designation);
          setJoiningDate(emp.joiningDate);
          setStatus(emp.status);
          setBankName(emp.bankName || '');
          setBankAccountNo(emp.bankAccountNo || '');
          setIfscCode(emp.ifscCode || '');
          setBasic(emp.salaryStructure.basic);
          setHra(emp.salaryStructure.hra);
          setConveyance(emp.salaryStructure.conveyance);
          setOtherAllowances(emp.salaryStructure.otherAllowances);
          setPfDeduction(emp.salaryStructure.pfDeduction);
          setEsiDeduction(emp.salaryStructure.esiDeduction);
          setTdsDeduction(emp.salaryStructure.tdsDeduction);
        }
      });
    }
  }, [employeeId]);

  useEffect(() => {
    if (nameInputRef.current) nameInputRef.current.focus();
  }, [employeeId]);

  const handleSave = async () => {
    if (!name.trim() || !empId.trim() || !activeCompany) {
      alert('Name and Employee ID are required');
      return;
    }

    const salaryStructure = {
      basic: Number(basic) || 0,
      hra: Number(hra) || 0,
      conveyance: Number(conveyance) || 0,
      otherAllowances: Number(otherAllowances) || 0,
      pfDeduction: Number(pfDeduction) || 0,
      esiDeduction: Number(esiDeduction) || 0,
      tdsDeduction: Number(tdsDeduction) || 0,
    };

    try {
      if (employeeId) {
        await db.employees.update(employeeId, {
          name: name.trim(),
          employeeId: empId.trim(),
          department: department.trim(),
          designation: designation.trim(),
          joiningDate,
          status,
          bankName: bankName.trim(),
          bankAccountNo: bankAccountNo.trim(),
          ifscCode: ifscCode.trim(),
          salaryStructure
        });
        handleItemSelect({ name: 'Quit' });
      } else {
        const exists = await db.employees.where({ companyId: activeCompany.id!, employeeId: empId.trim() }).first();
        if (exists) { alert('Employee ID already exists'); return; }
        await db.employees.add({
          companyId: activeCompany.id!,
          name: name.trim(),
          employeeId: empId.trim(),
          department: department.trim(),
          designation: designation.trim(),
          joiningDate,
          status,
          bankName: bankName.trim(),
          bankAccountNo: bankAccountNo.trim(),
          ifscCode: ifscCode.trim(),
          salaryStructure
        });
        
        // Reset
        setName('');
        setEmpId('');
        setDepartment('');
        setDesignation('');
        setJoiningDate('');
        setBankName('');
        setBankAccountNo('');
        setIfscCode('');
        setBasic(0);
        setHra(0);
        setConveyance(0);
        setOtherAllowances(0);
        setPfDeduction(0);
        setEsiDeduction(0);
        setTdsDeduction(0);
        if (nameInputRef.current) nameInputRef.current.focus();
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to save employee');
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'End' || (e.ctrlKey && e.key === 'a')) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleItemSelect({ name: 'Quit' });
    }
  };

  const totalEarnings = Number(basic) + Number(hra) + Number(conveyance) + Number(otherAllowances);
  const calculatedPF = (Number(basic) * Number(pfDeduction)) / 100;
  const calculatedESI = (Number(basic) * Number(esiDeduction)) / 100;
  const calculatedTDS = (Number(basic) * Number(tdsDeduction)) / 100;
  const totalDeductions = calculatedPF + calculatedESI + calculatedTDS;
  const netSalary = totalEarnings - totalDeductions;

  return (
    <div ref={containerRef} className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[13px] text-gray-800" onKeyDown={handleKeyDown}>
      <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px]">
        <span className="w-1/3 text-left">Employee {employeeId ? 'Alteration' : 'Creation'}</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" data-quit-btn onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>
      
      <div className="flex border-b border-[#c8d4d4] bg-[#f9fafb]">
        <div className="flex-1 flex flex-col p-4 pb-2 border-r border-[#c8d4d4]">
          <div className="flex items-center mb-1">
            <span className="w-[150px]">Employee Name</span><span className="w-[15px]">:</span>
            <input 
              ref={nameInputRef}
              type="text" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 max-w-[450px] border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold" 
              autoFocus 
            />
          </div>
          <div className="flex items-center mb-1">
            <span className="w-[150px]">Employee ID</span><span className="w-[15px]">:</span>
            <input 
              type="text" 
              value={empId}
              onChange={(e) => setEmpId(e.target.value)}
              className="flex-1 max-w-[200px] border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold" 
            />
          </div>
        </div>
      </div>
      
      <div className="flex flex-1 bg-[#fdfdfd] overflow-hidden">
        <div className="flex-1 flex flex-col p-4 border-r border-dashed border-[#c8d4d4] overflow-y-auto scrollbar-thin">
          <div className="font-bold underline decoration-black underline-offset-2 mb-3 text-black">General Details</div>
          <div className="flex items-center mb-2">
             <span className="w-[150px]">Department</span><span className="w-[15px]">:</span>
             <input type="text" value={department} onChange={(e) => setDepartment(e.target.value)} className="flex-1 max-w-[250px] border-b border-gray-300 outline-none" />
          </div>
          <div className="flex items-center mb-2">
             <span className="w-[150px]">Designation</span><span className="w-[15px]">:</span>
             <input type="text" value={designation} onChange={(e) => setDesignation(e.target.value)} className="flex-1 max-w-[250px] border-b border-gray-300 outline-none" />
          </div>
          <div className="flex items-center mb-2">
             <span className="w-[150px]">Joining Date</span><span className="w-[15px]">:</span>
             <input type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} className="border-b border-gray-300 outline-none" />
          </div>
          <div className="flex items-center mb-6">
             <span className="w-[150px]">Status</span><span className="w-[15px]">:</span>
             <select value={status ?? 'Active'} onChange={(e) => setStatus(e.target.value as any)} className="border-b border-gray-300 outline-none font-bold text-[#1b8086]">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
             </select>
          </div>
          
          <div className="font-bold underline decoration-black underline-offset-2 mb-3 text-black">Bank Details</div>
          <div className="flex items-center mb-2">
             <span className="w-[150px]">Bank Name</span><span className="w-[15px]">:</span>
             <input type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} className="flex-1 max-w-[250px] border-b border-gray-300 outline-none" />
          </div>
          <div className="flex items-center mb-2">
             <span className="w-[150px]">Account No.</span><span className="w-[15px]">:</span>
             <input type="text" value={bankAccountNo} onChange={(e) => setBankAccountNo(e.target.value)} className="flex-1 max-w-[250px] border-b border-gray-300 outline-none" />
          </div>
          <div className="flex items-center mb-2">
             <span className="w-[150px]">IFSC Code</span><span className="w-[15px]">:</span>
             <input type="text" value={ifscCode} onChange={(e) => setIfscCode(e.target.value)} className="flex-1 max-w-[250px] border-b border-gray-300 outline-none" />
          </div>
        </div>
        
        <div className="w-[450px] flex flex-col p-4 overflow-y-auto scrollbar-thin bg-[#f8fafc]">
           <div className="font-bold underline decoration-black underline-offset-2 mb-4 text-black">Salary Structure (Monthly)</div>
           
           <div className="flex flex-col gap-2 mb-6">
             <div className="flex items-center">
                <span className="w-[150px] text-gray-600">Basic Salary</span><span className="w-[15px]">:</span>
                <input type="number" value={basic || ''} onChange={(e) => setBasic(Number(e.target.value))} className="w-[120px] border-b border-gray-300 outline-none text-right font-bold" />
             </div>
             <div className="flex items-center">
                <span className="w-[150px] text-gray-600">HRA</span><span className="w-[15px]">:</span>
                <input type="number" value={hra || ''} onChange={(e) => setHra(Number(e.target.value))} className="w-[120px] border-b border-gray-300 outline-none text-right" />
             </div>
             <div className="flex items-center">
                <span className="w-[150px] text-gray-600">Conveyance</span><span className="w-[15px]">:</span>
                <input type="number" value={conveyance || ''} onChange={(e) => setConveyance(Number(e.target.value))} className="w-[120px] border-b border-gray-300 outline-none text-right" />
             </div>
             <div className="flex items-center">
                <span className="w-[150px] text-gray-600">Other Allowances</span><span className="w-[15px]">:</span>
                <input type="number" value={otherAllowances || ''} onChange={(e) => setOtherAllowances(Number(e.target.value))} className="w-[120px] border-b border-gray-300 outline-none text-right" />
             </div>
             <div className="flex items-center font-bold text-[#1e8585] mt-1 pt-1 border-t border-gray-200">
                <span className="w-[150px]">Total Earnings</span><span className="w-[15px]">:</span>
                <span className="w-[120px] text-right">{totalEarnings.toFixed(2)}</span>
             </div>
           </div>

           <div className="flex flex-col gap-2">
             <div className="flex items-center">
                <span className="w-[150px] text-red-600">PF Deduction (%)</span><span className="w-[15px]">:</span>
                <input type="number" value={pfDeduction || ''} onChange={(e) => setPfDeduction(Number(e.target.value))} className="w-[120px] border-b border-gray-300 outline-none text-right" />
                <span className="ml-2 text-gray-500 w-[80px] text-right">({((Number(basic) * Number(pfDeduction)) / 100).toFixed(2)})</span>
             </div>
             <div className="flex items-center">
                <span className="w-[150px] text-red-600">ESI Deduction (%)</span><span className="w-[15px]">:</span>
                <input type="number" value={esiDeduction || ''} onChange={(e) => setEsiDeduction(Number(e.target.value))} className="w-[120px] border-b border-gray-300 outline-none text-right" />
                <span className="ml-2 text-gray-500 w-[80px] text-right">({((Number(basic) * Number(esiDeduction)) / 100).toFixed(2)})</span>
             </div>
             <div className="flex items-center">
                <span className="w-[150px] text-red-600">TDS Deduction (%)</span><span className="w-[15px]">:</span>
                <input type="number" value={tdsDeduction || ''} onChange={(e) => setTdsDeduction(Number(e.target.value))} className="w-[120px] border-b border-gray-300 outline-none text-right" />
                <span className="ml-2 text-gray-500 w-[80px] text-right">({((Number(basic) * Number(tdsDeduction)) / 100).toFixed(2)})</span>
             </div>
             <div className="flex items-center font-bold text-red-700 mt-1 pt-1 border-t border-gray-200">
                <span className="w-[150px]">Total Deductions</span><span className="w-[15px]">:</span>
                <span className="w-[120px] text-right">{totalDeductions.toFixed(2)}</span>
             </div>
           </div>

           <div className="mt-auto pt-4 border-t-2 border-double border-gray-400">
              <div className="flex items-center font-bold text-[16px] text-black">
                <span className="w-[150px]">Net Salary</span><span className="w-[15px]">:</span>
                <span className="flex-1 text-right pr-4">₹ {netSalary.toFixed(2)}</span>
              </div>
           </div>
        </div>
      </div>
      
      <div className="flex justify-between items-center bg-[#f1f5f9] border-t border-gray-300 px-4 py-2 text-[12px]">
          <div className="italic text-gray-400">Press End or Ctrl+A to Accept | Esc to Cancel</div>
          <div className="flex items-center gap-4">
            <button data-save-btn onClick={handleSave} className="bg-[#1e8585] text-white px-4 py-1 rounded font-bold hover:bg-[#156666]">Save (End)</button>
          </div>
      </div>
    </div>
  );
};
